import { Popover, PopoverContent, PopoverTrigger, Portal } from '@chakra-ui/react';
import { StyledElementProps, useEditorRef } from '@udecode/plate';
import { Emoji, EmojiData, Picker } from 'emoji-mart';
import * as React from 'react';
import { Node, Transforms } from 'slate';
import { ReactEditor, useReadOnly } from 'slate-react';

import { DEFAULT_EMOJI } from './constants';

const { memo, useState, useCallback, Fragment, useMemo } = React;

export const CalloutElementBase = memo(
  ({ attributes, children, element, nodeProps }: StyledElementProps) => {
    const readOnly = useReadOnly();
    const [isEmojiOpen, setIsEmojiOpen] = useState(false);
    const editor = useEditorRef();

    const { emojiInfo } = element;

    const handleChangeEmoji = useCallback(
      (_emojiInfo: EmojiData) => {
        const path = ReactEditor.findPath(editor as ReactEditor, element);
        Transforms.setNodes(editor, { emojiInfo: _emojiInfo } as Partial<Node>, { at: path });
        handleCloseEmoji();
      },
      [editor, element]
    );

    const handleOpenEmoji = useCallback(() => {
      setIsEmojiOpen(true);
    }, []);
    const handleCloseEmoji = useCallback(() => {
      setIsEmojiOpen(false);
    }, []);

    const emoji = useMemo(() => {
      return (
        <Popover
          onOpen={handleOpenEmoji}
          isOpen={readOnly ? false : isEmojiOpen}
          modifiers={[{ name: 'eventListeners', options: { scroll: isEmojiOpen } }]}
          onClose={handleCloseEmoji}
        >
          {({ isOpen }) => (
            <Fragment>
              <PopoverTrigger>
                <button>
                  <Emoji
                    emoji={emojiInfo ? (emojiInfo as any) : DEFAULT_EMOJI}
                    set="apple"
                    size={24}
                  />
                </button>
              </PopoverTrigger>
              <Portal>
                <PopoverContent className="focus:shadow-outline">
                  {isOpen ? (
                    <Picker
                      title="Pick your emoji…"
                      emoji="point_up"
                      onSelect={handleChangeEmoji}
                    />
                  ) : null}
                </PopoverContent>
              </Portal>
            </Fragment>
          )}
        </Popover>
      );
    }, [isEmojiOpen, emojiInfo, handleChangeEmoji]);

    return (
      <div
        className="pt-16 pr-32 pb-27 pl-58 flex my-4 relative bg-gray-100 rounded-lg leading-24"
        data-node="callout-element"
        {...attributes}
        {...nodeProps}
      >
        <span>{children}</span>
        <div contentEditable={false} className="mr-10 absolute top-20 left-20">
          {emoji}
        </div>
      </div>
    );
  }
);

CalloutElementBase.displayName = 'callout-element-base';

/**
 * CalloutTipElement
 */
export const CalloutElement = CalloutElementBase;
